
import { IPTEForm } from "@/models/PTE";
import { createDateRangeRuleWithinLast } from "@/utils/rules";
import { TInputRule } from "@nationbetter/nb-components/src/models/Input";
import { computed, defineComponent, getCurrentInstance, onMounted, ref, watch } from "vue-demi";

const FORM_MODEL: IPTEForm = {
  date: "",
  family_name: "",
  first_name: "",
  listening: "",
  reading: "",
  speaking: "",
  selt_urn: "",
  writing: "",
};

const MASKS = {
  score: "NN",
};

export default defineComponent({
  name: "FormPTE",

  props: {
    loading: { type: Boolean, default: false },
    details: { type: Object as () => IPTEForm, required: true },
  },

  emits: ["update"],

  setup(props, { emit }) {
    const instance = getCurrentInstance()?.proxy;
    const form = ref<IPTEForm>({ ...FORM_MODEL });

    const requiredRules = [(v: string) => v.length || String(instance?.$t("errors.required"))];

    const scoreMask = MASKS.score;

    const scoreRules: TInputRule[] = [
      (v) => !!v.length || String(instance?.$t("errors.required")),
      (v) => Number(v) >= 59 || String(instance?.$t("errors.score")),
      (v) => {
        const str = String(v);
        return str[str.length - 1] !== "." || String(instance?.$t("errors.lastDotChar"));
      },
    ];

    const dateRules = [
      createDateRangeRuleWithinLast({ years: 2 }, String(instance?.$t("errors.date"))),
      (v: string) => !!v?.length || String(instance?.$t("errors.required")),
    ];

    const isFormValid = computed(() => {
      const {
        family_name,
        first_name,
        selt_urn,
        listening,
        reading,
        writing,
        speaking,
        date,
      } = form.value;

      const scores = [listening, reading, writing, speaking];
      const fields = [family_name, first_name, selt_urn, ...scores, date];

      const allFieldsFilled = fields.every((v) => v !== "");
      const allScoresValid = scores.every((v) => Number(v) >= 59);
      const isDateValid = dateRules.every((rule) => rule(date) === true);

      return allFieldsFilled && allScoresValid && isDateValid;
    });

    const setCosForm = (data: IPTEForm) => {
      Object.assign(form.value, { ...data });
    };

    onMounted(() => {
      setCosForm(props.details);

      emit("update", {
        form: form.value,
        valid: isFormValid.value,
      });
    });

    watch(
      () => form.value,
      () => {
        emit("update", {
          form: form.value,
          valid: isFormValid.value,
        });
      },
      { deep: true, immediate: true }
    );

    return {
      form,
      requiredRules,
      scoreRules,
      scoreMask,
      dateRules,
      isFormValid,
    };
  },
});
